/**
 * Pure helpers behind the latency panels in `PerformancePanels.tsx` (kept
 * separate so that file only exports components).
 */
import { format } from 'date-fns';
import type { ApiStatsSeriesPoint } from '../types';

export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds <= 0) return '0s';
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  if (seconds < 60) return `${seconds.toFixed(2)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export interface LatencyPoint {
  label: string;
  p50: number;
  p75: number;
  p90: number;
  queue: number;
  boot: number;
}

/**
 * Expand the sparse server series into evenly-spaced buckets covering the whole
 * window. Buckets with no calls have no latency to report and are drawn at zero
 * so the line stays continuous and isolated spikes remain visible.
 */
export function buildLatencySeries(
  series: ApiStatsSeriesPoint[] | undefined,
  metric: 'exec' | 'startup',
  rangeMs: number,
  stepMs: number,
  labelFmt: string,
): LatencyPoint[] {
  const end = Date.now();
  const start = Math.floor((end - rangeMs) / stepMs) * stepMs;
  const map = new Map<number, LatencyPoint>();
  for (let t = start; t <= end; t += stepMs) {
    map.set(t, {
      label: format(new Date(t), labelFmt),
      p50: 0,
      p75: 0,
      p90: 0,
      queue: 0,
      boot: 0,
    });
  }
  for (const point of series || []) {
    if (!point.ts) continue;
    const bucket = Math.floor(new Date(point.ts).getTime() / stepMs) * stepMs;
    const entry = map.get(bucket);
    if (!entry) continue;
    entry.p50 = (metric === 'exec' ? point.exec_p50 : point.startup_p50) ?? 0;
    entry.p75 = (metric === 'exec' ? point.exec_p75 : point.startup_p75) ?? 0;
    entry.p90 = (metric === 'exec' ? point.exec_p90 : point.startup_p90) ?? 0;
    entry.queue = point.queue_p90 ?? 0;
    entry.boot = point.boot_p90 ?? 0;
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([, v]) => v);
}
